import router from '../../router';
import { countWords, escapeHTML } from '../../utils/text';
import { storyModes } from '@/utils/mappers.js';
import Vue from 'vue';
import {
  categories,
  actions as analyticsActions,
} from '@/constants/analytics.js';
import { getEnvVar } from '@/utils/getEnvVar';
import {
  formatExtraFieldsForDisplay,
  formatExtraFieldsForStorage,
  formatTableExtraFields,
} from '../../utils/extraFieldsFormatter';
import { sortOrder } from '../../utils/mappers';
import storejs from 'store';

const FEEDBACK_MESSAGES = {
  DEFAULT: 'Texto copiado para área de transferência.',
  LOCKED: 'A pauta já está sendo editada.',
  TOO_MANY_REQUESTS:
    'O número de licenças para edição atingiu o limite! Por favor, aguarde alguns minutos e tente novamente.',
  UPDATE_SUCCESS: 'Pauta atualizada com sucesso.',
  UPDATE_ERROR: 'Ops... Ocorreu um erro ao atualizar a pauta.',
  CREATE_SUCCESS: 'Pauta criada com sucesso.',
  CREATE_ERROR: 'Ops... Ocorreu um erro ao criar a pauta.',
  NO_ACCESS_ERROR:
    'Ops... Usuário não tem permissão para criar retrancas nesta gaveta.',
  REORDER_SUCCESS: 'Retrancas reordenadas no iNews com sucesso!',
  REORDER_ERROR:
    'Ops... Ocorreu um erro ao tentar reordenar as retrancas no iNews.',
  DELETE_SUCCESS: 'Pauta deletada com sucesso.',
  ARCHIVED_STORY: 'Esta pauta já foi arquivada e não pode mais ser editada.',
  UNKNOWN_ERROR:
    'Ops... Tivemos um problema no servidor. Por favor, tente novamente em alguns instantes :)',
  CONNECTION_ERROR: 'Sem conexão',
  LOADING_EDITION:
    'Preparando o modo de edição... Por favor, aguarde alguns instantes :)',
};

const shouldSearchConflicts = story => {
  const min_words_conflict = parseInt(getEnvVar('VUE_APP_MIN_WORDS_CONFLICT'));
  return countWords(escapeHTML(story.data.body)) > min_words_conflict;
};

const shouldSearchVideo = story => {
  const min_words_video = parseInt(getEnvVar('VUE_APP_VIDEO_SEARCH_MIN_WORDS'));
  return countWords(escapeHTML(story.data.body)) > min_words_video;
};

const handleDefaultErrors = (error, commit) => {
  if (!error.response) {
    commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.CONNECTION_ERROR);
  } else {
    if (error.response.status === 401) {
      router.push('/login');
    } else {
      commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.UNKNOWN_ERROR);
    }
  }
};

const EMPTY_STORY = {
  data: {
    story_id: '',
    version_id: '',
    title: '',
    body: '',
    author: '',
    creation_date: '',
    modified_date: '',
    modified_by: '',
    path: '',
    place: '',
    extra_fields: {},
  },
};

const PAGE_SIZE = 20;

const getters = {
  snackbarBox: state => state.snackbarBox,
};

const state = {
  news: [],
  folderNews: [],
  selectedStory: EMPTY_STORY,
  // Remove snackbar from module state (REFACTOR)
  snackbarBox: false,
  feedbackMessage: FEEDBACK_MESSAGES.DEFAULT,
  nextPage: 1,
  storyConflicts: {
    total: 0,
  },
  conflictsNextPage: 2,
  storyMode: storyModes.VIEW,
  noFolderAccessCard: false,
  noFolderAccessTable: false,
  reorderPermission: false,
  writePermission: false,
  systemFolders: [],
  loadingFolders: true,
  isFetchingNews: true,
  isFetchingFields: false,
  fetchNewsError: false,
  isWaitingConflicts: false,
  isWaitingMoreConflicts: false,
  isLoadingCreation: false,
  isLoadingEdition: false,
  connectingToInews: false,
  writeFolders: [],
  fieldMapping: [],
};

const actions = {
  getNews(
    { commit, state, rootState: root },
    {
      query,
      fromDate,
      toDate,
      author,
      folders,
      contact,
      order = sortOrder.DESC,
    },
  ) {
    commit('SET_FETCHING_NEWS', true);
    commit('SET_FETCHING_NEWS_ERROR', false);
    const token = root.user.currentUser.token;

    function getNewsConflicts(data) {
      data.news.forEach(story => {
        root.pautaClient
          .searchConflicts(token, story.story_id, 1)
          .then(response =>
            commit('UPDATE_NEWS_CONFLICTS', {
              response: response,
              story: story,
            }),
          );
      });
    }

    root.pautaClient
      .search({
        token,
        pageSize: PAGE_SIZE,
        page: state.nextPage,
        query,
        fromDate,
        toDate,
        author,
        folders,
        contact,
        order,
      })
      .then(data => {
        if (!data.news.length && !state.news.length) {
          commit('SET_FETCHING_NEWS_ERROR', true);
        }
        getNewsConflicts(data);
        commit('UPDATE_NEWS', data);
        commit('SET_NO_FOLDER_ACCESS_CARD', false);
      })
      .catch(error => {
        commit('SET_FETCHING_NEWS_ERROR', true);
        if (!error.response) {
          commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.CONNECTION_ERROR);
        } else {
          switch (error.response.status) {
            case 401:
              router.push('/login');
              break;
            case 403:
              commit('SET_NO_FOLDER_ACCESS_CARD', true);
              router.push('/');
              break;
            default:
              commit(
                'UPDATE_FEEDBACK_MESSAGE',
                FEEDBACK_MESSAGES.UNKNOWN_ERROR,
              );
          }
        }
      })
      .finally(() => {
        commit('SET_FETCHING_NEWS', false);
      });
  },
  getFolderContent({ commit, dispatch, rootState }, path) {
    const token = rootState.user.currentUser.token;
    const place = path.split('.')[0];
    const folder = path
      .split('.')
      .slice(1)
      .join('.');
    commit('SET_FETCHING_NEWS', true);

    rootState.pautaClient
      .getFolderContent(token, place, folder)
      .then(data => {
        commit('UPDATE_FOLDER_NEWS', data.news);
        commit('UPDATE_REORDER_PERMISSION', data.reorder_allowed);
        commit('UPDATE_WRITE_PERMISSION', data.write_allowed);
        commit('SET_NO_FOLDER_ACCESS_TABLE', false);
        dispatch('updateFieldMapping', {
          place: place,
          folder: folder,
          format: 'table',
        });
      })
      .catch(error => {
        switch (error.response.status) {
          case 401:
            router.push('/login');
            break;
          case 403:
            commit('SET_NO_FOLDER_ACCESS_TABLE', true);
            break;
          default:
            commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.UNKNOWN_ERROR);
        }
      })
      .finally(() => {
        commit('SET_FETCHING_NEWS', false);
      });
  },
  selectHistoricStory({ commit, dispatch, rootState }, { story_id, contact }) {
    const token = rootState.user.currentUser.token;
    commit('SET_FETCHING_NEWS', true);

    rootState.pautaClient
      .getHistoricStory(token, story_id, contact)
      .then(story => {
        if (shouldSearchConflicts(story)) {
          dispatch('getStoryConflicts', story.story_id);
        }
        dispatch('videos/resetVideos', {}, { root: true });
        if (shouldSearchVideo(story)) {
          dispatch(
            'videos/searchRelatedVideos',
            {
              title: story.data.title,
              body: story.data.body,
              isManualSearch: false,
            },
            { root: true },
          );
        }
        commit('UPDATE_SELECTED_STORY', story);
        dispatch('updateFieldMapping', {
          place: story.data.place,
          folder: story.data.path,
          format: 'story',
        });
      })
      .catch(error => {
        if (!error.response) {
          commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.CONNECTION_ERROR);
        } else {
          if (error.response.status === 401) {
            router.push('/login');
          } else {
            router.push('/');
          }
        }
        commit('UPDATE_SNACKBAR', true);
      })
      .finally(() => {
        commit('SET_FETCHING_NEWS', false);
      });
  },

  selectStory({ commit, dispatch, rootState }, { story_id }) {
    const token = rootState.user.currentUser.token;

    commit('SET_FETCHING_NEWS', true);
    rootState.pautaClient
      .getStory(token, story_id)
      .then(story => {
        if (shouldSearchConflicts(story)) {
          dispatch('getStoryConflicts', story.story_id);
        }
        dispatch('videos/resetVideos', {}, { root: true });
        if (shouldSearchVideo(story)) {
          dispatch(
            'videos/searchRelatedVideos',
            {
              title: story.data.title,
              body: story.data.body,
              isManualSearch: false,
            },
            { root: true },
          );
        }
        commit('UPDATE_SELECTED_STORY', story);
        dispatch('updateFieldMapping', {
          place: story.data.place,
          folder: story.data.path,
          format: 'story',
        });
      })
      .catch(error => {
        if (!error.response) {
          commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.CONNECTION_ERROR);
        } else {
          if (error.response.status === 401) {
            router.push('/login');
          } else {
            router.push('/');
          }
        }
        commit('UPDATE_SNACKBAR', true);
      })
      .finally(() => {
        commit('SET_FETCHING_NEWS', false);
      });
  },
  getStoryConflicts({ commit, rootState }, story_id = null) {
    const token = rootState.user.currentUser.token;
    commit('SET_WAITING_CONFLICTS', true);
    rootState.pautaClient
      .searchConflicts(token, story_id, PAGE_SIZE)
      .then(data => commit('SET_STORY_CONFLICTS', data))
      .catch(error => {
        handleDefaultErrors(error, commit);
      })
      .finally(() => {
        commit('SET_WAITING_CONFLICTS', false);
      });
  },
  getNextConflicts({ commit, rootState, state }, story = null) {
    const token = rootState.user.currentUser.token;
    commit('SET_WAITING_MORE_CONFLICTS', true);
    rootState.pautaClient
      .searchConflicts(
        token,
        story.story_id,
        PAGE_SIZE,
        state.conflictsNextPage,
      )
      .then(data => commit('UPDATE_STORY_CONFLICTS', data.news))
      .catch(error => {
        handleDefaultErrors(error, commit);
      })
      .finally(() => commit('SET_WAITING_MORE_CONFLICTS', false));
  },
  getSlugToEdit({ commit, state, rootState }, story = null) {
    commit('SET_LOADING_EDITION', true);

    const token = rootState.user.currentUser.token;
    return rootState.pautaClient
      .getSlugToEdit(token, story)
      .then(data => {
        const extraFieldsFormatted = formatExtraFieldsForDisplay(
          data.extra_fields,
          state.fieldMapping,
        );
        const hotStoryData = {
          ...state.selectedStory.data,
          ...data,
          extra_fields: extraFieldsFormatted,
        };
        commit('UPDATE_SELECTED_STORY_DATA', hotStoryData);
        commit('SET_STORY_MODE', storyModes.EDITION);
        return true;
      })
      .catch(error => {
        if (!error.response) {
          commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.CONNECTION_ERROR);
        } else {
          switch (error.response.status) {
            case 401:
              router.push('/login');
              break;
            case 403:
            case 503:
              commit('UPDATE_FEEDBACK_MESSAGE', error.response.data.detail);
              break;
            case 423:
              commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.LOCKED);
              break;
            default:
              commit(
                'UPDATE_FEEDBACK_MESSAGE',
                FEEDBACK_MESSAGES.UNKNOWN_ERROR,
              );
          }
        }
        commit('UPDATE_SNACKBAR', true);

        return false;
      })
      .finally(() => {
        commit('SET_LOADING_EDITION', false);
      });
  },
  cancelEdition({ commit }) {
    commit('SET_LOADING_EDITION', true);
    commit('SET_STORY_MODE', storyModes.VIEW);
    commit('SET_LOADING_EDITION', false);
  },

  deleteStory({ commit, rootState }, story) {
    const token = rootState.user.currentUser.token;
    commit('SET_FETCHING_NEWS', true);

    rootState.pautaClient
      .deleteStory(token, story)
      .then(() => {
        commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.DELETE_SUCCESS);
        commit('UPDATE_SNACKBAR', true);
        router.push('/');
      })
      .catch(error => {
        handleDefaultErrors(error, commit);
        commit('UPDATE_SNACKBAR', true);
      })
      .finally(() => {
        commit('SET_FETCHING_NEWS', false);
      });
  },
  unsetStory({ commit }) {
    commit('UPDATE_SELECTED_STORY', EMPTY_STORY);
  },
  snackbarTrigger({ commit }, value) {
    commit('UPDATE_SNACKBAR', value);
  },
  updateFeedbackMessage({ commit }, message) {
    commit('UPDATE_FEEDBACK_MESSAGE', message);
  },
  unsetStoryConflicts({ commit }) {
    commit('UNSET_STORY_CONFLICTS');
  },
  reset({ commit }) {
    commit('RESET');
  },
  setStoryMode({ commit }, value) {
    commit('SET_STORY_MODE', value);
  },

  getSystemFoldersTree({ commit, rootState }, { filter = {} } = {}) {
    const token = rootState.user.currentUser.token;
    return rootState.pautaClient
      .loadSystemFoldersTree(token, filter)
      .then(data => {
        commit('UPDATE_SYSTEM_FOLDERS_TREE', data.data);
      });
  },
  getWriteFolders({ commit, rootState }) {
    const token = rootState.user.currentUser.token;
    rootState.pautaClient
      .getWriteFolders(token)
      .then(data => {
        commit('SET_WRITE_FOLDERS', data);
      })
      .catch(error => {
        handleDefaultErrors(error, commit);
        commit('UPDATE_SNACKBAR', true);
      });
  },
  createStory({ dispatch, commit, rootState }, story) {
    commit('SET_LOADING_CREATION', true);
    const token = rootState.user.currentUser.token;
    story.extra_fields = {
      ...formatExtraFieldsForStorage(
        story.extra_fields,
        rootState.news.fieldMapping,
      ),
    };
    rootState.pautaClient
      .createStory(token, story)
      .then(data => {
        commit('UPDATE_SELECTED_STORY', {
          data: data.data,
          story_id: data.data.story_id,
        });
        dispatch(
          'analytics/analyticsEvent',
          {
            action: analyticsActions.creationSuccessful,
            category: categories.create,
            label: data.data.story_id,
          },
          { root: true },
        );

        commit('SET_STORY_MODE', storyModes.VIEW);

        router.replace(
          {
            path: `/story/${data.data.story_id}`,
            query: { slug_id: data.data.slug_id || data.data.id },
          },
          () => {
            router.go(0);
          },
        );

        commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.CREATE_SUCCESS);
        commit('UPDATE_SNACKBAR', true);
      })
      .catch(error => {
        if (error.response.status == 403) {
          commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.NO_ACCESS_ERROR);
        } else {
          handleDefaultErrors(error, commit);
        }
        commit('UPDATE_SNACKBAR', true);
      })
      .finally(() => {
        commit('SET_LOADING_CREATION', false);
      });
  },
  reorderStories({ commit, rootState }, stories) {
    const token = rootState.user.currentUser.token;
    commit('SET_FETCHING_NEWS', true);
    const place = stories[0].place;

    rootState.pautaClient
      .reorderStories(token, stories, place)
      .then(() => {
        commit('UPDATE_FOLDER_NEWS', stories);
        commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.REORDER_SUCCESS);
        commit('UPDATE_SNACKBAR', true);
      })
      .catch(error => {
        handleDefaultErrors(error, commit);
        commit('UPDATE_SNACKBAR', true);
      })
      .finally(() => {
        commit('SET_FETCHING_NEWS', false);
      });
  },
  updateStory({ commit, dispatch, rootState }, { story, contact }) {
    const token = rootState.user.currentUser.token;
    const username = rootState.user.currentUser.username;
    commit('SET_LOADING_EDITION', true);
    story.extra_fields = {
      ...formatExtraFieldsForStorage(
        story.extra_fields,
        rootState.news.fieldMapping,
      ),
    };
    rootState.pautaClient
      .updateStory(token, story)
      .then(() => {
        commit('SET_STORY_MODE', storyModes.VIEW);
        storejs.remove(
          `draft_${username}_${story.place}_${story.path}_${story.slug_id}`,
        );
        dispatch(
          'analytics/analyticsEvent',
          {
            category: categories.edit,
            action: analyticsActions.editSuccessful,
            label: story.slug_id,
          },
          { root: true },
        );
        dispatch('selectStory', {
          story_id: story.slug_id,
          contact: contact,
        });
        commit('UPDATE_FEEDBACK_MESSAGE', FEEDBACK_MESSAGES.UPDATE_SUCCESS);
        commit('UPDATE_SNACKBAR', true);
      })
      .catch(error => {
        handleDefaultErrors(error, commit);
        commit('UPDATE_SNACKBAR', true);
      })
      .finally(() => {
        commit('SET_LOADING_EDITION', false);
      });
  },
  updateFieldMapping({ commit, rootState }, { place, folder, format }) {
    const token = rootState.user.currentUser.token;
    commit('SET_FETCHING_FIELDS', true);
    rootState.pautaClient
      .getFolderFields(token, place, folder)
      .then(response => {
        if (format === 'story') {
          const newExtraFields = formatExtraFieldsForDisplay(
            rootState.news.selectedStory.data.extra_fields,
            response.data,
          );
          commit('UPDATE_EXTRA_FIELDS', newExtraFields);
        } else if (format === 'table') {
          const newTableData = formatTableExtraFields(
            rootState.news.folderNews,
            response.data,
          );
          commit('UPDATE_FOLDER_NEWS', newTableData);
        }
        commit('UPDATE_FIELD_MAPPING', response.data);
      })
      .catch(err => {
        commit('UPDATE_FIELD_MAPPING', []);
        handleDefaultErrors(err, commit);
      })
      .finally(() => {
        commit('SET_FETCHING_FIELDS', false);
      });
  },
  resetFieldMapping({ commit }) {
    commit('UPDATE_FIELD_MAPPING', []);
  },
};

const mutations = {
  UPDATE_NEWS(state, items) {
    items.news.forEach(el => {
      if (!el.data.conflicts) {
        el.data.conflicts = 0;
      }
    });
    state.news = [...state.news, ...items.news];
    state.nextPage++;
  },
  UPDATE_FOLDER_NEWS(state, items) {
    state.folderNews = items;
  },
  UPDATE_REORDER_PERMISSION(state, value) {
    state.reorderPermission = value || false;
  },
  UPDATE_WRITE_PERMISSION(state, value) {
    state.writePermission = value || false;
  },
  UPDATE_SELECTED_STORY(state, story) {
    state.selectedStory = story;
  },
  UPDATE_SELECTED_STORY_DATA(state, storyData) {
    state.selectedStory = {
      ...state.selectedStory,
      data: storyData,
    };
  },
  UPDATE_SNACKBAR(state, value) {
    state.snackbarBox = value;
  },
  UPDATE_FEEDBACK_MESSAGE(state, value = FEEDBACK_MESSAGES.DEFAULT) {
    state.feedbackMessage = value;
  },
  SET_STORY_CONFLICTS(state, value) {
    state.storyConflicts = value;
  },
  UPDATE_STORY_CONFLICTS(state, conflicts) {
    state.storyConflicts.news = [...state.storyConflicts.news, ...conflicts];
    state.conflictsNextPage++;
  },
  UNSET_STORY_CONFLICTS(state) {
    state.storyConflicts = { total: 0 };
    state.conflictsNextPage = 2;
  },
  RESET(state) {
    state.news = [];
    state.nextPage = 1;
  },
  SET_STORY_MODE(state, value) {
    state.storyMode = value;
  },
  SET_NO_FOLDER_ACCESS_CARD(state, value) {
    state.noFolderAccessCard = value;
  },
  SET_NO_FOLDER_ACCESS_TABLE(state, value) {
    state.noFolderAccessTable = value;
  },
  UPDATE_NEWS_CONFLICTS(state, payload) {
    const index = state.news.findIndex(
      item => item.story_id === payload.story.story_id,
    );
    let mutatedStory = state.news[index];
    if (mutatedStory) {
      mutatedStory.data.conflicts = payload.response.total;
      Vue.set(state.news, index, mutatedStory);
    }
  },
  UPDATE_SYSTEM_FOLDERS_TREE(state, folders) {
    state.systemFolders = folders;
    state.loadingFolders = false;
  },
  UPDATE_CLICKED_ON_CARD(state, value) {
    state.clickedOnCard = value;
  },
  SET_WAITING_CONFLICTS(state, isWaiting) {
    state.isWaitingConflicts = isWaiting;
  },
  SET_WAITING_MORE_CONFLICTS(state, isWaiting) {
    state.isWaitingMoreConflicts = isWaiting;
  },
  SET_FETCHING_NEWS(state, value) {
    state.isFetchingNews = value;
  },
  SET_FETCHING_FIELDS(state, value) {
    state.isFetchingFields = value;
  },
  SET_FETCHING_NEWS_ERROR(state, payload) {
    state.fetchNewsError = payload;
  },
  SET_LOADING_EDITION(state, value) {
    state.isLoadingEdition = value;
  },
  SET_LOADING_CREATION(state, value) {
    state.isLoadingCreation = value;
  },
  SET_CONNECTING_TO_INEWS(state, value) {
    state.connectingToInews = value;
  },
  SET_WRITE_FOLDERS(state, value) {
    state.writeFolders = value;
  },
  UPDATE_EXTRA_FIELDS(state, value) {
    state.selectedStory.data.extra_fields = value;
  },
  UPDATE_FIELD_MAPPING(state, value) {
    state.fieldMapping = value;
  },
};

export default {
  getters,
  state,
  actions,
  mutations,
  namespaced: true,
};
